import React, { useCallback, useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import { getCatalogUploadTemplate } from '@src/apis/reware/catalog';

interface CsvDropZoneProps {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  showDownloadTemplate?: boolean;
  partnerCode?: string;
  onError?: (message: string) => void;
}

const ACCEPTED_EXTENSIONS = ['.csv', '.tsv'];

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

const CsvDropZone: React.FC<CsvDropZoneProps> = ({
  onFileSelected,
  disabled = false,
  showDownloadTemplate = false,
  partnerCode,
  onError,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isAcceptedFile(file)) {
        onFileSelected(file);
      } else {
        onError?.('Invalid file type. Please upload a CSV or TSV file.');
      }
    }
    // Reset input so re-selecting the same file triggers onChange
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      const file = event.dataTransfer.files?.[0];
      if (file) {
        if (isAcceptedFile(file)) {
          onFileSelected(file);
        } else {
          onError?.('Invalid file type. Please upload a CSV or TSV file.');
        }
      }
    },
    [disabled, onFileSelected, onError],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDownloadTemplate = async () => {
    if (!partnerCode) {
      onError?.('Partner code not available');
      return;
    }

    setIsDownloading(true);
    try {
      await getCatalogUploadTemplate(partnerCode);
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <Box
        component="label"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 6,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Upload CSV or TSV File
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag &amp; drop or click to select
        </Typography>
        <input
          type="file"
          hidden
          accept=".csv,.tsv"
          onChange={handleFileSelect}
          disabled={disabled}
          aria-label="Select CSV or TSV file for upload"
        />
      </Box>

      {showDownloadTemplate && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={disabled || isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Template'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CsvDropZone;
